import type { IngredientCategory } from "../domain/types";
import { TablerIcon } from "./TablerIcon";

/**
 * Only ingredients with a literal, unambiguous Tabler icon are listed here;
 * every other ingredient falls back to the plain category dot so an icon
 * never implies a false visual identity. See TablerIcon for sourcing.
 */
const ICON_PATHS: Record<string, string[]> = {
  carrot: [
    "M3 21s9.834 -3.489 12.684 -6.34a4.487 4.487 0 0 0 0 -6.344a4.483 4.483 0 0 0 -6.342 0c-2.86 2.861 -6.347 12.689 -6.347 12.689l.005 -.005",
    "M9 13l-1.5 -1.5",
    "M16 14l-2 -2",
    "M22 8s-1.14 -2 -3 -2c-1.406 0 -3 2 -3 2s1.14 2 3 2s3 -2 3 -2",
    "M16 2s-2 1.14 -2 3s2 3 2 3s2 -1.577 2 -3c0 -1.86 -2 -3 -2 -3",
  ],
  "bell-pepper": [
    "M13 11c0 2.21 -2.239 4 -5 4s-5 -1.79 -5 -4a8 8 0 1 0 16 0a3 3 0 0 0 -6 0",
    "M16 8c0 -2 2 -4 4 -4",
  ],
  mushroom: [
    "M20 11.1c0 -4.474 -3.582 -8.1 -8 -8.1s-8 3.626 -8 8.1a.9 .9 0 0 0 .9 .9h14.2a.9 .9 0 0 0 .9 -.9",
    "M10 12v7a2 2 0 1 0 4 0v-7",
  ],
  egg: [
    "M19 14.083c0 4.154 -2.966 6.74 -7 6.917c-4.2 0 -7 -2.763 -7 -6.917c0 -5.538 3.5 -11.09 7 -11.083c3.5 .007 7 5.545 7 11.083",
  ],
  bread: [
    "M18 4a3 3 0 0 1 2 5.235v8.765a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-8.764a3 3 0 0 1 1.824 -5.231h12.176v-.005",
  ],
  feta: [
    "M4.519 20.008l16.481 -.008v-3.5a2 2 0 1 1 0 -4v-3.5h-16.722",
    "M21 9l-9.385 -4.992c-2.512 .12 -4.758 1.42 -6.327 3.425c-1.423 1.82 -2.288 4.221 -2.288 6.854c0 2.117 .56 4.085 1.519 5.721",
    "M15 13v.01",
    "M8 13v.01",
    "M11 16v.01",
  ],
  avocado: [
    "M17.8 14.04a3.905 3.905 0 0 1 1.337 -2.075c1.195 -.985 1.816 -2.285 1.863 -3.902c-.047 -1.43 -.54 -2.626 -1.477 -3.586c-.96 -.938 -2.156 -1.43 -3.585 -1.477c-1.618 .047 -2.918 .668 -3.903 1.863c-.562 .68 -1.254 1.125 -2.074 1.336c-.938 .188 -1.828 .48 -2.672 .88c-.844 .398 -1.559 .878 -2.144 1.44c-1.43 1.501 -2.145 3.224 -2.145 5.169c0 1.946 .715 3.668 2.145 5.168c1.5 1.429 3.222 2.144 5.168 2.144c1.945 0 3.667 -.715 5.167 -2.145c.563 -.585 1.055 -1.3 1.477 -2.144c.398 -.844 .68 -1.723 .844 -2.637v-.035l-.001 .001",
    "M10.87 10.036c-.942 .112 -1.794 .538 -2.556 1.278c-.74 .762 -1.166 1.614 -1.278 2.556c-.135 .92 .112 1.704 .74 2.354c.65 .628 1.435 .875 2.354 .74c.942 -.112 1.794 -.538 2.556 -1.278c.74 -.762 1.166 -1.614 1.278 -2.556c.135 -.92 -.112 -1.704 -.74 -2.354c-.65 -.628 -1.435 -.875 -2.354 -.74",
  ],
  lemon: [
    "M17.536 3.393c3.905 3.906 3.905 10.237 0 14.143c-3.906 3.905 -10.237 3.905 -14.143 0l14.143 -14.143",
    "M5.868 15.06a6.5 6.5 0 0 0 9.193 -9.192",
    "M10.464 10.464l4.597 4.597",
    "M10.464 10.464v6.364",
    "M10.464 10.464h6.364",
  ],
  salt: [
    "M12 13v.01",
    "M10 16v.01",
    "M14 16v.01",
    "M7.5 8h9l-.281 -2.248a2 2 0 0 0 -1.985 -1.752h-4.468a2 2 0 0 0 -1.986 1.752l-.28 2.248",
    "M7.5 8l-1.612 9.671a2 2 0 0 0 1.973 2.329h8.278a2 2 0 0 0 1.973 -2.329l-1.612 -9.671",
  ],
};

interface Props {
  ingredientId: string;
  category: IngredientCategory;
}

export function IngredientGlyph({ ingredientId, category }: Props) {
  const paths = ICON_PATHS[ingredientId];
  if (!paths) {
    return (
      <span
        className={`ingredient-dot ingredient-dot--${category}`}
        aria-hidden="true"
      />
    );
  }
  return (
    <TablerIcon
      paths={paths}
      className={`ingredient-icon ingredient-icon--${category}`}
    />
  );
}
